import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSearchStore from '../../stores/searchStore';
import InfoModal from '../../modals/info-modal/info-modal';
import useInvestorDetailStore from '../../stores/investorDetailStore';
import { SPACES_ENDPOINT } from '../../utils/constants';
import LogoLight from '../../assets/images/logo/logo-light.svg';
import LogoMulticolor from '../../assets/images/logo/logo-multicolor.svg';
import {
    Navbar,
    NavbarTitle,
    //SearchContainer,
    //SearchInput,
    //SearchIcon,
    Avatar,
    TopbarLogo,
    AvatarDropdownItem,
    AvatarDropdownContainer,
    DropdownSubtitle,
    DropdownTitle,
    DropdownHeader,
    DropdownItemLabel,
    InitialsAvatar,
    Progress,
    ProgressBarContainer,
    AvatarWrapper,
    NotificationDot
} from './old-topbar.styles';
import MaterialIcon from '../material-icon';
import { colorPalette } from '../../utils/theme';
import { logout } from '../../services/authService';
import useConstructionUpdateStore from '../../stores/constructionUpdateStore';
import useUnitDetailStore from '../../stores/unitDetailStore';
import useDashboardStore from '../../stores/dashboardStore';
import useUnitStore from '../../stores/unitStore';
import { useLocation } from 'react-router-dom';
import { usePWA } from '../../context/PWAContext';
import useIsMobile from '../../hooks/useIsMobile';
import useCustomNavigate from '../../hooks/useCustomNavigate';
import useTopbarStore from '../../stores/topbarStore';
import { DocumentStatus, DocumentType } from '../../services/documentService';
import { useTranslation } from 'react-i18next';
import { tStr } from '../../utils/i18n-helpers';
import { Capacitor } from '@capacitor/core';
import useTenantDetailStore from '../../stores/tenantDetailStore';

interface TopbarProps {
    isFullPage?: boolean;
    deactivateSidebar?: boolean;
    theme?: 'light' | 'dark';
    toggleSidebar: () => void;
}

const OldTopbar: React.FC<TopbarProps> = ({ isFullPage = false, deactivateSidebar = false, theme = "light", toggleSidebar }) => {
    const navigate = useCustomNavigate();
    const location = useLocation();
    const isPWA = usePWA();
    const isCapacitor = Capacitor.isNativePlatform();
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    // Stores
    const { investor, reset: resetInvestoreDetailStore } = useInvestorDetailStore();
    const { tenant, reset: resetTenantDetailStore } = useTenantDetailStore();
    const { search, setSearch } = useSearchStore();
    const { isProgressBarVisible, progress } = useTopbarStore();
    const resetUnitStore = useUnitStore(state => state.reset);
    const resetUnitDetailStore = useUnitDetailStore(state => state.reset);
    const resetConstructionUpdateStore = useConstructionUpdateStore(state => state.reset);
    const resetDashboardStore = useDashboardStore(state => state.reset);

    // States
    const [isHistoryAvailable, setIsHistoryAvailable] = useState(false);
    const [title, setTitle] = useState<string>("");
    const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
    const [isInfoModalVisible, setIsInfoModalVisible] = useState<boolean>(false);
    const [showDropdown, setShowDropdown] = useState<boolean>(false);

    // Set Title
    useEffect(() => {
        setIsSearchVisible(false);
        const depth = (navigate as any).getHistoryDepth?.();
        setIsHistoryAvailable(depth > 0);
    }, [location, t]);

    useEffect(() => {
        if (investor) {
            setTitle(tStr(t, "components.topbar.title"));
        } else if (tenant) {
            setTitle(tStr(t, "components.topbar.tenant_title"));
        }
    }, [investor, tenant, t]);

    const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    }, [setSearch]);

    // Functions
    const onClickLogout = async () => {
        await logout();

        resetUnitStore();
        resetConstructionUpdateStore();
        resetInvestoreDetailStore();
        resetTenantDetailStore();
        resetUnitDetailStore();
        resetDashboardStore();

        navigate("/login", { replace: true, clearHistory: true });
    };

    // Memos
    const initials = useMemo(() => {
        if (!investor && !tenant) return '';
        const names = `${investor ? investor.first_name : tenant?.first_name} ${investor ? investor.last_name : tenant?.last_name}`.split(' ').filter(name => name.trim().length > 0);
        return names.slice(0, 2).map(name => name[0]?.toUpperCase() || '').join('');
    }, [investor, tenant]);

    const themeColor = useMemo(() => {
        if (theme === "dark") {
            return colorPalette.white;
        } else {
            return colorPalette.textMain;
        }
    }, [theme]);

    // Investor Account Memos
    const hasAddress = useMemo(() => {
        if (!investor) return false;
        if (tenant) return true;
        return investor.address.country !== null || investor.address.country !== undefined;
    }, [investor, tenant]);

    const expiredDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.valid_until !== null && new Date(doc.valid_until) < new Date();
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.valid_until !== null && new Date(doc.valid_until) < new Date();
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const declinedDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.status === DocumentStatus.DECLINED;
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.status === DocumentStatus.DECLINED;
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const inReviewDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.status === DocumentStatus.IN_REVIEW;
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.status === DocumentStatus.IN_REVIEW;
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const hasMandatoryDocumentsUploaded = useMemo(() => {
        if (investor) {
            return investor.documents.filter(document => document.document_type === DocumentType.PASSPORT || document.document_type === DocumentType.PROOF_OF_ADDRESS).length > 1
        } else if (tenant) {
            return tenant.documents.filter(document => document.document_type === DocumentType.PASSPORT || document.document_type === DocumentType.EMIRATES_ID).length > 1
        } else {
            return false;
        }
    }, [investor, tenant]);

    // Header States
    const headerStatus = useMemo(() => {
        if (!hasAddress && !hasMandatoryDocumentsUploaded) return "inactive";
        if (!hasMandatoryDocumentsUploaded || expiredDocuments.length > 0 || declinedDocuments.length > 0) return "inactive";
        if (inReviewDocuments.length > 0) return "in_review";
        return "active";
    }, [expiredDocuments, hasAddress, hasMandatoryDocumentsUploaded, declinedDocuments, inReviewDocuments]);

    return (
        <>
            <Navbar className="navbar navbar-expand-lg navbar-dark" themeVariant={theme}>
                <div className="container-fluid d-flex justify-content-between me-md-3">
                    {isFullPage ? (
                        <div className="d-flex align-items-center" style={{ gap: 8, cursor: "pointer" }}>
                            {isMobile && isHistoryAvailable && (
                                <MaterialIcon icon="arrow_back_ios" color={themeColor} size={20} onClick={() => navigate(-1)} />
                            )}
                            <TopbarLogo src={theme === "dark" ? LogoLight : LogoMulticolor} alt="Logo" onClick={() => navigate(location.pathname === "/reset-password" ? "/login" : "/", { replace: true, clearHistory: true })} />
                        </div>

                    ) : (
                        <div className="d-flex d-lg-none align-items-center" style={{ gap: 8 }}>
                            {isMobile && isHistoryAvailable && (
                                <MaterialIcon icon="arrow_back_ios" color={themeColor} size={20} onClick={() => navigate(-1)} />
                            )}
                            <TopbarLogo className="d-lg-none d-block" src={theme === "dark" ? LogoLight : LogoMulticolor} onClick={() => navigate("/", { replace: true, clearHistory: true })} />
                        </div>
                    )}

                    {!isFullPage && (
                        <div>
                            <NavbarTitle className="ms-2 d-none d-lg-block">{title}</NavbarTitle>
                        </div>
                    )}

                    <div className="d-flex align-items-center">
                        {/*isSearchVisible && (
                        <div className="d-none d-lg-flex align-items-center">
                            <SearchContainer className="me-3">
                                <SearchIcon />
                                <SearchInput
                                    type="search"
                                    placeholder={tStr(t, "components.topbar.search_placeholder")}
                                    aria-label="Search"
                                    value={search}
                                    onChange={handleSearchInputChange}
                                />
                            </SearchContainer>
                        </div>
                    )*/}
                        {/*<NotificationIcon
                        className="d-none d-lg-block"
                        onClick={() => setIsInfoModalVisible(true)}
                    />*/}

                        {!deactivateSidebar && !isPWA && !isCapacitor && (
                            <MaterialIcon className="d-flex d-lg-none" icon="menu" color={themeColor} size={28} onClick={toggleSidebar} />
                        )}

                        {isMobile && (isPWA || isCapacitor) && investor && !location.pathname.includes("/profile") && (
                            <AvatarWrapper style={{ height: 23, width: 23 }}>
                                <MaterialIcon icon="account_circle" color={themeColor} size={24} onClick={() => navigate("/profile", { replace: true, clearHistory: true })} />

                                {/* Show notification dot if `hasNotification` is true */}
                                {headerStatus === "inactive" && <NotificationDot style={{ height: 10, width: 10 }} />}
                            </AvatarWrapper>
                        )}

                        {/* Avatar and Dropdown */}
                        <div
                            onMouseEnter={() => setShowDropdown(true)}
                            onMouseLeave={() => setShowDropdown(false)}
                            className="position-relative d-none d-lg-block"
                        >
                            {investor ? (
                                <AvatarWrapper>
                                    {investor?.avatar ? (
                                        <Avatar
                                            showBorder={showDropdown}
                                            src={`${SPACES_ENDPOINT}${investor.avatar}`}
                                            onClick={() => navigate("/profile", { replace: true, clearHistory: true })}
                                        />
                                    ) : (
                                        <InitialsAvatar
                                            showBorder={showDropdown}
                                            onClick={() => navigate("/profile", { replace: true, clearHistory: true })}
                                        >
                                            {initials}
                                        </InitialsAvatar>
                                    )}

                                    {/* Show notification dot if `hasNotification` is true */}
                                    {headerStatus === "inactive" && <NotificationDot />}
                                </AvatarWrapper>
                            ) : tenant ? (
                                <AvatarWrapper>
                                    <InitialsAvatar
                                        showBorder={showDropdown}
                                        onClick={() => navigate("/profile", { replace: true, clearHistory: true })}
                                    >
                                        {initials}
                                    </InitialsAvatar>

                                    {/* Show notification dot if `hasNotification` is true */}
                                    {headerStatus === "inactive" && <NotificationDot />}
                                </AvatarWrapper>
                            ) : null}

                            {showDropdown && (
                                <AvatarDropdownContainer>
                                    <DropdownHeader>
                                        <DropdownTitle className="mb-0">{investor ? investor.first_name : tenant?.first_name} {investor ? investor.last_name : tenant?.last_name}</DropdownTitle>
                                        <DropdownSubtitle>{investor ? investor.email : tenant?.email}</DropdownSubtitle>
                                    </DropdownHeader>
                                    <AvatarDropdownItem onClick={() => {
                                        setShowDropdown(false);
                                        navigate("/profile", { replace: true, clearHistory: true });
                                    }}>
                                        <MaterialIcon icon="account_circle" size={20} color={colorPalette.textMain} />
                                        <DropdownItemLabel>{tStr(t, "components.topbar.my_account")}</DropdownItemLabel>
                                    </AvatarDropdownItem>
                                    <AvatarDropdownItem onClick={() => {
                                        setShowDropdown(false);
                                        navigate("/profile/settings");
                                    }}>
                                        <MaterialIcon icon="manage_accounts" size={20} color={colorPalette.textMain} />
                                        <DropdownItemLabel>{tStr(t, "components.topbar.settings")}</DropdownItemLabel>
                                    </AvatarDropdownItem>
                                    <AvatarDropdownItem onClick={onClickLogout}>
                                        <MaterialIcon icon="logout" size={20} color={colorPalette.textMain} />
                                        <DropdownItemLabel>{tStr(t, "components.topbar.logout")}</DropdownItemLabel>
                                    </AvatarDropdownItem>
                                </AvatarDropdownContainer>
                            )}
                        </div>
                    </div>
                </div>
                {isInfoModalVisible && (
                    <InfoModal onClose={() => setIsInfoModalVisible(false)} />
                )}
            </Navbar>

            {isProgressBarVisible && (
                <ProgressBarContainer>
                    <Progress width={progress} />
                </ProgressBarContainer>
            )}
        </>
    );
};

export default OldTopbar;
